import type { CSSProperties } from 'styled-components';
import { keyframes } from 'styled-components';
import { theme } from '@frameio/vapor-theme';

const contentScaleOut = 0.5;
const doneScaleOut = 0.2;

const contentOutDuration = 0.2;
const backgroundOutDuration = 0.55;

const defaultAnimationStyles = {
  animationFillMode: 'both',
  animationIterationCount: 1,
};

const disableAnimationStyles = {
  animationDuration: '0s',
  animationDelay: '0s',
  animationFillMode: 'both',
};

const endFrames: { out: CSSProperties; in: CSSProperties } = {
  out: {
    scale: contentScaleOut,
    visibility: 'hidden',
  },
  in: {
    scale: 1.0,
    visibility: 'visible',
  },
};

export const getContentStyles = ({
  show,
  mounted,
}: {
  show: boolean;
  mounted: boolean;
}): CSSProperties => {
  if (!mounted) {
    return disableAnimationStyles;
  }

  return {
    ...defaultAnimationStyles,
    animationDelay: `${show ? backgroundOutDuration : 0}s`,
    animationDuration: `${contentOutDuration}s`,
    animationTimingFunction: show
      ? theme.transitions.eases.fastStart
      : theme.transitions.eases.slowStart,
  };
};

export const getDoneStyles = ({
  show,
  mounted,
}: {
  show: boolean;
  mounted: boolean;
}): CSSProperties => {
  if (!mounted) {
    return disableAnimationStyles;
  }
  return {
    ...defaultAnimationStyles,
    animationDelay: `${show ? contentOutDuration : 0}s`,
    animationDuration: `${show ? 0.25 : 0.2}s`,
    animationTimingFunction: show
      ? theme.transitions.eases.fastStart
      : theme.transitions.eases.slowStart,
  };
};

export const getBackgroundStyles = ({
  show,
  mounted,
}: {
  show: boolean;
  mounted: boolean;
}): CSSProperties => {
  if (!mounted) {
    const endFrame = show ? endFrames.in : endFrames.out;
    return {
      ...disableAnimationStyles,
      ...endFrame,
    };
  }

  return {
    ...defaultAnimationStyles,
    animationDelay: `${show ? contentOutDuration + 0.2 : 0}s`,
    animationDuration: `${show ? backgroundOutDuration : 0.4}s`,
    animationTimingFunction: show
      ? theme.transitions.eases.easeOut
      : theme.transitions.eases.slowStart,
  };
};

export const animateContent = {
  out: keyframes`
  0% {
    scale: 1.0;
    visibility: visible;
  }
  99% {
    scale: ${contentScaleOut};
    visibility: hidden;
  }
  100% {
    scale: ${contentScaleOut};
    visibility: hidden;
  }
`,
  in: keyframes`
  0% {
    scale: ${contentScaleOut};
    visibility: hidden;
  }
  1% {
    scale: ${contentScaleOut};
    visibility: visible;
  }
  100% {
    scale: 1.0;
    visibility: visible;
  }
`,
};

export const animateDone = {
  in: keyframes`
  0% {
    scale: ${doneScaleOut};
    visibility: hidden;
  }
  1% {
    scale: ${doneScaleOut};
    visibility: visible;
  }
  100% {
    scale: 1.0;
    visibility: visible;
  }
`,
  out: keyframes`
  0% {
    scale: 1.0;
  }
  99% {
    scale: ${contentScaleOut};
  }
  100% {
    scale: ${contentScaleOut};
    visibility: hidden;
  }
  `,
};

export const animateBackground = {
  in: keyframes`
  0% {
    visibility: hidden;
    scale: ${0.75};
  }
  1% {
    scale: ${0.75};
    visibility: visible;
  }
  100% {
    scale: 1.0;
    visibility: visible;
  }
`,
  out: keyframes`
  0% {
    scale: 1.0;
  }
  99% {
    scale: ${contentScaleOut};
    visibility: visible;
  }
  100% {
    scale: ${contentScaleOut};
    visibility: hidden;
  }
`,
};
